import type {
	TransferRecordState,
	getTransferRecordsFacetsOutput,
	getTransferRecordsPlayerFacetsOutput,
} from "@/api/custom_types";
import { toFacetFilterOptions } from "@/components";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import type { FacetCounts } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { getTransferRecordStatusTranslation } from "@/utils/translations";
import {
	Card,
	CardContent,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";

export interface FilterFormData {
	orderEmail: string;
	senderEmail: string;
	states: string[];
	players: string[];
}

const EMPTY: FilterFormData = {
	orderEmail: "",
	senderEmail: "",
	states: [],
	players: [],
};

interface FilterFormProps {
	onSearch: (values: FilterFormData) => void;
	defaultValues: FilterFormData;
	facetCounts: FacetCounts<getTransferRecordsFacetsOutput>;
	players?: getTransferRecordsPlayerFacetsOutput;
}

const TransactionsFilter: React.FC<FilterFormProps> = ({
	onSearch,
	defaultValues,
	facetCounts,
	players,
}) => {
	const { t } = useTranslation();

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<FilterFormData>({
			initialFilters: defaultValues,
			onApply: onSearch,
			textFilterKeys: ["orderEmail", "senderEmail"],
		});

	const statusOptions = useMemo(
		() =>
			toFacetFilterOptions<TransferRecordState>(
				facetCounts.statuses,
				(state) => getTransferRecordStatusTranslation(state, t).text as string,
			),
		[facetCounts.statuses, t],
	);

	const playerOptions = useMemo(
		() =>
			(players ?? []).map((p) => ({
				value: String(p.resource_id),
				label: [p.name, p.rating, p.rarity_name].filter(Boolean).join(" "),
				counter: String(p.count),
			})),
		[players],
	);

	const config = useMemo<FilterConfig<FilterFormData>>(
		() => ({
			fields: [
				{
					key: "orderEmail",
					type: "search",
					placeholder: t("transactions.filterOrderEmail"),
				},
				{
					key: "senderEmail",
					type: "search",
					placeholder: t("transactions.filterSenderEmail"),
				},
				{
					key: "states",
					type: "multi-select",
					placeholder: t("transactions.filterStatus"),
					options: statusOptions,
					searchable: true,
				},
				{
					key: "players",
					type: "multi-select",
					placeholder: t("transactions.filterPlayer"),
					options: playerOptions,
					searchable: true,
				},
			],
			searchDebounceMs: 0,
			showClearButton: true,
		}),
		[t, statusOptions, playerOptions],
	);

	return (
		<Card className="w-full">
			<CardContent>
				<GenericFilters
					config={config}
					filters={filters}
					onFiltersChange={onFiltersChange}
					onClearFilters={() => clearFilters(EMPTY)}
				/>
			</CardContent>
		</Card>
	);
};

export default TransactionsFilter;
